import { createReducer } from 'reduxsauce';
import { compose } from 'ramda';
import { INITIAL_STATE, INITIAL_STATUS_PROPS } from './initialState';
import {
  FETCH_COMMENTS_REQUEST,
  FETCH_COMMENTS_SUCCESS,
  FETCH_COMMENTS_FAILURE,
  FETCH_PARENT_COMMENTS_REQUEST,
  FETCH_PARENT_COMMENTS_SUCCESS,
  FETCH_PARENT_COMMENTS_FAILURE,
  FETCH_CHILD_COMMENTS_REQUEST,
  CANCEL_CHILD_COMMENTS_REQUEST,
  FETCH_CHILD_COMMENTS_SUCCESS,
  FETCH_CHILD_COMMENTS_FAILURE,
  LIKE_COMMENT_REQUEST,
  LIKE_COMMENT_SUCCESS,
  LIKE_COMMENT_FAILURE,
  UNLIKE_COMMENT_REQUEST,
  UNLIKE_COMMENT_SUCCESS,
  UNLIKE_COMMENT_FAILURE,
  LIKE_CHILD_COMMENT_REQUEST,
  LIKE_CHILD_COMMENT_SUCCESS,
  LIKE_CHILD_COMMENT_FAILURE,
  UNLIKE_CHILD_COMMENT_REQUEST,
  UNLIKE_CHILD_COMMENT_SUCCESS,
  UNLIKE_CHILD_COMMENT_FAILURE,
  POST_COMMENT_REQUEST,
  POST_COMMENT_SUCCESS,
  POST_COMMENT_FAILURE,
  POST_REPLY_REQUEST,
  POST_REPLY_SUCCESS,
  POST_REPLY_FAILURE,
  DELETE_COMMENT_REQUEST,
  DELETE_COMMENT_FAILURE,
  DELETE_COMMENT_SUCCESS,
  DELETE_REPLY_REQUEST,
  DELETE_REPLY_FAILURE,
  DELETE_REPLY_SUCCESS,
  TOGGLE_COMMENT_EDITING,
  EDIT_PARENT_REQUEST,
  EDIT_CHILD_REQUEST,
  EDIT_CHILD_SUCCESS,
  EDIT_PARENT_SUCCESS,
  EDIT_CHILD_FAILURE,
  EDIT_PARENT_FAILURE,
  CLEAR_POST_COMMENT_ERROR,
  CLEAR_POST_REPLY_ERROR,
  REPORT_COMMENT_REQUEST,
  REPORT_COMMENT_FAILURE,
  REPORT_REPLY_REQUEST,
  REPORT_REPLY_FAILURE,
  LOAD_MORE_PARENT_COMMENTS_REQUEST,
  LOAD_MORE_PARENT_COMMENTS_SUCCESS,
  LOAD_MORE_PARENT_COMMENTS_FAILURE,
  LOAD_MORE_CHILDREN_COMMENTS_SUCCESS,
  LOAD_MORE_CHILDREN_COMMENTS_FAILURE,
  LOAD_MORE_CHILDREN_COMMENTS_REQUEST,
  CLEAR_EDIT_COMMENT_ERROR
} from '../../actions/comments/commentsActions';
import {
  updateCommentById,
  updateChildCommentById,
  updateLoadMoreChildrenCommentsRequest
} from './commentsUpdaterFunctions';
import {
  flattenChildrenInComments,
  addStatusProps,
  updateChildLoadingPropsInComment,
  childLoadingComments
} from '../../utils/utils';
import {
  TRANSLATE_PARENT_REQUEST,
  TRANSLATE_PARENT_FAILURE,
  TRANSLATE_CHILD_REQUEST,
  TRANSLATE_CHILD_SUCCESS,
  TRANSLATE_CHILD_FAILURE,
  TOGGLE_PARENT_TRANSLATION,
  TOGGLE_CHILD_TRANSLATION,
  TRANSLATE_PARENT_SUCCESS
} from '../../actions/comments/translation/translationActions';
import {
  translateParentRequest,
  translateParentSuccess,
  translateParentFailure,
  translateChildRequest,
  translateChildSuccess,
  translateChildFailure,
  toggleParentTranslation,
  toggleChildTranslation
} from './translation/translationReducer';
import { COMMENTS_CONTEXT_SOCIAL_FEED } from '../../../utils/constants/commentConstants';

const flattenAndDecorateComments = compose(
  flattenChildrenInComments,
  addStatusProps
);

export const fetchCommentsRequest = (state) => {
  return {
    ...state,
    isFetching: true,
    success: false
  };
};

export const fetchCommentsSuccess = (state, action) => {
  const {
    payload: { comments }
  } = action;
  return {
    ...state,
    comments: flattenAndDecorateComments(comments, INITIAL_STATUS_PROPS),
    error: false,
    isFetching: false,
    success: true
  };
};

export const fetchCommentsFailure = (state, action) => {
  const {
    payload: { errorType }
  } = action;

  return {
    ...state,
    error: true,
    errorType,
    isFetching: false,
    success: false
  };
};

export const fetchParentCommentsRequest = (state) => {
  return {
    ...state,
    error: false,
    errorType: null,
    isFetching: true,
    success: false
  };
};

/**
 * In this reducer instead of flattening the data with `flattenAndDecorateComments` we only want to set status props.
 * For example the edit and child loading props calling the `addStatusProps`.
 *
 * @param {Object} state the data that exists in the redux layer of the app
 * @param {Object} action the event triggered
 */
export const fetchParentCommentsSuccess = (state, action) => {
  const {
    payload: { comments, isLastPage }
  } = action;
  return {
    ...state,
    comments: addStatusProps(comments, INITIAL_STATUS_PROPS),
    error: false,
    errorType: null,
    isFetching: false,
    isLastPage,
    success: true
  };
};

export const fetchParentCommentsFailure = (state, action) => {
  const {
    payload: { errorType }
  } = action;

  return {
    ...state,
    error: true,
    errorType,
    isFetching: false,
    success: false
  };
};

export const fetchChildCommentsRequest = (state, action) => {
  const { comments } = state;
  const { parentId } = action.payload;

  const updateChildLoading = {
    loading: true,
    error: false,
    errorType: null
  };

  return {
    ...state,
    comments: updateChildLoadingPropsInComment(
      comments,
      parentId,
      updateChildLoading
    )
  };
};

export const cancelChildCommentsRequest = (state, action) => {
  const { comments } = state;
  const {
    payload: { parentId }
  } = action;

  return {
    ...state,
    comments: updateChildLoadingPropsInComment(
      comments,
      parentId,
      INITIAL_STATUS_PROPS.childLoading
    )
  };
};

export const fetchChildCommentsSuccess = (state, action) => {
  const { comments } = state;
  const {
    payload: { parentId, childComments, isLastPage, totalCount }
  } = action;

  const commentWithChildren = childLoadingComments(
    comments,
    parentId,
    childComments
  );

  return {
    ...state,
    comments: updateChildLoadingPropsInComment(
      comments,
      parentId,
      { ...INITIAL_STATUS_PROPS.childLoading, isLastPage, totalCount },
      commentWithChildren
    )
  };
};

export const fetchChildCommentsFailure = (state, action) => {
  const { comments } = state;
  const { parentId } = action.payload;

  const updateChildLoading = {
    loading: false,
    error: true,
    errorType: FETCH_CHILD_COMMENTS_FAILURE
  };

  return {
    ...state,
    comments: updateChildLoadingPropsInComment(
      comments,
      parentId,
      updateChildLoading
    )
  };
};

const setLikePropsInComment = (comments, commentId) => {
  return comments.map((comment) => {
    if (comment.id === commentId) {
      const { liked_by_me: likeByMe, likes_count: likesCount } = comment;

      return {
        ...comment,
        liked_by_me: !likeByMe,
        likes_count: likeByMe ? likesCount - 1 : likesCount + 1
      };
    }

    return { ...comment };
  });
};

const updateChildCommentLikes = (comments, commentId, parentId) => {
  const parentComment = comments.find((comment) => comment.id === parentId);

  return comments.map((comment) => {
    if (typeof parentComment === 'undefined') return { ...comment };

    if (comment.id === parentId) {
      return {
        ...comment,
        children: setLikePropsInComment(parentComment.children, commentId)
      };
    }
    return { ...comment };
  });
};

const updateCommentLikes = (comments, commentId, parentId) => {
  return parentId
    ? updateChildCommentLikes(comments, commentId, parentId)
    : setLikePropsInComment(comments, commentId);
};

export const likeCommentRequest = (state, action) => {
  const { commentId } = action.payload;
  const { comments } = state;

  return {
    ...state,
    comments: updateCommentLikes(comments, commentId), // optimistic comment update
    error: false
  };
};

export const likeCommentSuccess = (state) => {
  return {
    ...state,
    error: false
  };
};

export const likeCommentFailure = (state, action) => {
  const {
    payload: { commentId }
  } = action;
  const { comments } = state;

  return {
    ...state,
    error: true,
    comments: updateCommentLikes(comments, commentId)
  };
};

export const likeChildCommentRequest = (state, action) => {
  const { parentId, commentId } = action.payload;
  const { comments } = state;

  return {
    ...state,
    comments: updateCommentLikes(comments, commentId, parentId), // optimistic comment update
    error: false
  };
};

export const likeChildCommentSuccess = (state) => {
  return {
    ...state,
    error: false
  };
};

export const likeChildCommentFailure = (state, action) => {
  const {
    payload: { parentId, commentId }
  } = action;
  const { comments } = state;

  return {
    ...state,
    error: true,
    comments: updateCommentLikes(comments, commentId, parentId)
  };
};

export const unlikeCommentRequest = (state, action) => {
  const {
    payload: { commentId }
  } = action;
  const { comments } = state;

  return {
    ...state,
    // Opportunistic UI update
    comments: updateCommentLikes(comments, commentId),
    error: false
  };
};

export const unlikeCommentSuccess = (state) => {
  return {
    ...state,
    error: false
  };
};

export const unlikeCommentFailure = (state, action) => {
  const {
    payload: { commentId }
  } = action;

  const { comments } = state;

  return {
    ...state,
    comments: updateCommentLikes(comments, commentId),
    error: true
  };
};

export const unlikeChildCommentRequest = (state, action) => {
  const {
    payload: { parentId, commentId }
  } = action;
  const { comments } = state;

  return {
    ...state,
    // Opportunistic UI update
    comments: updateCommentLikes(comments, commentId, parentId),
    error: false
  };
};

export const unlikeChildCommentSuccess = (state) => {
  return {
    ...state,
    error: false
  };
};

export const unlikeChildCommentFailure = (state, action) => {
  const {
    payload: { parentId, commentId }
  } = action;

  const { comments } = state;

  return {
    ...state,
    comments: updateCommentLikes(comments, commentId, parentId),
    error: true
  };
};

export const postCommentRequest = (state) => {
  return {
    ...state,
    isPosting: true,
    error: false,
    errorType: null
  };
};

export const postCommentSuccess = (state, action) => {
  // needs to add the new comment into the store, whilst these are now ordered we'll add it as the last item anyway.
  const {
    payload: { comment, commentsContext }
  } = action;

  const commentWithAddedProps = {
    ...comment,
    ...INITIAL_STATUS_PROPS
  };

  return {
    ...state,
    comments:
      commentsContext === COMMENTS_CONTEXT_SOCIAL_FEED
        ? [commentWithAddedProps]
        : [...state.comments, commentWithAddedProps],
    isPosting: false,
    error: false,
    errorType: null
  };
};

export const postCommentFailure = (state, action) => {
  const {
    payload: { errorType }
  } = action;
  return {
    ...state,
    isPosting: false,
    error: true,
    errorType
  };
};

/**
 * Function that generates new comments array for when a comment reply is successful.
 * Will add the reply to the end of the root level parent children and will also set the replyPosted prop to true on the parent comment (parent/root can be the same for non children replies)
 * @param {Object} requestInfo Information about the reques.
 * @param {Array.<Object>} requestInfo.comments The existing comments (won't be mutated).
 * @param {number} requestInfo.rootId  The top level comment ID.
 * @param {number=} requestInfo.parentID Optional parentId comment ID (if different to root level ID indicates that comment is a reply to a child).
 * @param {Object} requestInfo.reply The new reply object to add to the store.
 * @returns {Array.<Object>} a new (non-mutated) version of the comments array with the relevant comment replyPosted props set to false.
 */
const generateCommentsReplySuccess = (
  { comments, rootId, parentId = null, reply },
  commentsContext
) => {
  // Function that will amend the replyPosted prop to true, in a children comments array for a given id.
  // I know that this function has access to the parentId from the parent scope but I just prefer pure functions ;)
  const amendChildren = (childrenComments, _parentId) => {
    if (!_parentId) {
      return [...childrenComments];
    }
    const returnArray = childrenComments.map((item) => {
      if (item.id === _parentId) {
        return {
          ...item,
          replyPosted: true
        };
      }
      return { ...item };
    });
    return returnArray;
  };
  const wasChildReply = parentId && parentId !== rootId;

  return comments.map((item) => {
    // Replace the item that matches the id
    if (item.id === rootId) {
      // Avoid error when posting comment
      // and then replying to that comment
      const { descendant_ids: descendantIds = [] } = item;
      if (wasChildReply) {
        return {
          ...item,
          children: [...amendChildren(item.children, parentId), reply],
          descendant_ids: [...descendantIds, reply.id]
        };
      }
      return {
        ...item,
        children:
          commentsContext === COMMENTS_CONTEXT_SOCIAL_FEED
            ? [reply]
            : [...item.children, reply],
        descendant_ids: [...descendantIds, reply.id],
        replyPosted: true
      };
    }
    // Leave every other item unchanged
    return { ...item };
  });
};

/**
 * Function that generates new comments array for when a comment reply is requested.
 * The key change is the addition/setting of the replyPosted prop which allows the component to track the success action.
 * @param {Object} requestInfo Information about the reques.
 * @param {Array.<Object>} requestInfo.comments The existing comments (won't be mutated).
 * @param {number} requestInfo.rootId  The top level comment ID.
 * @param {number=} requestInfo.parentID Optional parentId comment ID (if different to root level ID indicates that comment is a reply to a child).
 * @returns {Array.<Object>} a new (non-mutated) version of the comments array with the relevant comment replyPosted props set to false.
 */
const generateCommentsReplyRequest = ({
  comments,
  rootId,
  parentId = null
}) => {
  // I used an iterator as for replies to children you have to repeat the map on the children. this way saves writting another map for that purpose.
  // very simply: without the innerId argument the second internal/recursive call to the iterator won't happen.
  // If the first iteration finds a innerId it recalls the function with that as the outerId and no innerId passed.
  // This works as we don't have multiple levels of nested replies in the comments client redux store.
  const replyRequestIterator = (commentArray, outerId, innerId = null) => {
    return commentArray.map((item) => {
      if (item.id === outerId) {
        if (innerId && innerId !== outerId) {
          return {
            ...item,
            children: replyRequestIterator(item.children, innerId)
          };
        }
        return {
          ...item,
          replyPosted: false
        };
      }
      return { ...item };
    });
  };
  return replyRequestIterator(comments, rootId, parentId);
};

/**
 * Iterates through the parent comments and flags whichever one was
 * being replied to with an error status
 * @param {Array} comments Parent comments
 * @param {Number} rootId The root/parent comment where the reply failed
 */
const generateCommentsReplyFailure = ({ comments, rootId }) =>
  comments.map((item) => {
    if (item.id === rootId) {
      return {
        ...item,
        replyError: true,
        replyPosted: false
      };
    }
    return item;
  });

const clearReplyErrors = ({ comments, rootId }) =>
  comments.map((item) => {
    if (item.id === rootId) {
      return {
        ...item,
        replyError: false
      };
    }
    return item;
  });

export const postReplyRequest = (state, action) => {
  const {
    payload: { parentId, rootId }
  } = action;
  const generateCommentsArgs = {
    comments: [...state.comments],
    rootId,
    parentId
  };
  return {
    ...state,
    comments: generateCommentsReplyRequest(generateCommentsArgs),
    isPosting: true
  };
};

export const postReplySuccess = (state, action) => {
  const {
    payload: { reply, commentsContext }
  } = action;
  const parentId = reply.ancestry.parent_comment_id;
  const rootId = reply.ancestry.root_comment_id;
  const generateCommentsArgs = {
    comments: state.comments,
    rootId,
    parentId,
    reply: {
      ...reply,
      // This is a temp state to tell the client that it was recently added by the user
      // We use this to determine whether we should render components above or below
      // this comments. See RepliesContainer for more information on how the conditional
      // rendering is done
      recentlyAddedByUser: true
    }
  };
  return {
    ...state,
    comments: generateCommentsReplySuccess(
      generateCommentsArgs,
      commentsContext
    ),
    isPosting: false,
    error: false,
    errorType: null
  };
};

export const postReplyFailure = (state, action) => {
  const {
    payload: { rootId }
  } = action;
  const { comments } = state;
  const newComments = generateCommentsReplyFailure({ comments, rootId });
  return {
    ...state,
    comments: newComments,
    isPosting: false
  };
};

// The following are a few utility functions that I found useful for generating new comments arrays for the delete requests (could be useful for other similar actions??)
// They couldn't be composed or curried as they involve mapping an array at their heart.

/**
 * This function will decorate a comment with props related to requesting a comment to be deleted.
 * Returns a new object and does not mutate the comment argument.
 * @param {object} comment The comment object to be decorated.
 * @return {object} A copy of the comment passed with the deletion request props added/set.
 */
const updateDeleteRequestComment = (comment) => {
  return {
    ...comment,
    deletion: {
      ...comment.deletion,
      completed: false,
      error: false
    }
  };
};

/**
 * This function will decorate a comment with props related to a request for a comment to be deleted failing.
 * Returns a new object and does not mutate the comment argument.
 * @param {object} comment The comment object to be decorated.
 * @return {object} A copy of the comment passed with the deletion request props added/set.
 */
const updateDeleteFailureComment = (comment) => {
  return {
    ...comment,
    deletion: {
      ...comment.deletion,
      completed: true,
      error: true
    }
  };
};

/**
 * This function will decorate a comment with props related to a request for a comment to be deleted being successful.
 * Returns a new object and does not mutate the comment argument.
 * @param {object} comment The comment object to be decorated.
 * @return {object} A copy of the comment passed with the deletion request props added/set.
 */
const updateDeleteSuccessComment = (comment) => {
  return {
    ...comment,
    deleted: true,
    deletion: {
      ...comment.deletion,
      completed: true,
      error: false
    }
  };
};

/**
 * Toggles the editing state of a comment
 * so that the change will be reflected in the UI.
 * @param {Object} params.editing Hash of editing status props
 * @param {Object} params.comment Remaining comment object properties
 */
const toggleEditingState = ({ editing = {}, ...comment }) => ({
  ...comment,
  body: editing.previousComment || comment.body,
  editing: {
    ...editing,
    isEditing: !editing.isEditing
  }
});

/**
 * This will amend a comment in the store
 * that has been submitted for editing. The
 * state will update immediately with the
 * new comment. The previous comment is preserved
 * in case the request fails and things have to be reverted.
 * @param {Object} comment Comment object
 * @param {String} commentText The new comment text to replace the current comment
 */
const updateEditedComment = (comment, commentText) => ({
  ...comment,
  body: commentText,
  editing: {
    // If we have a previousComment keep it. Otherwise set it to what the current body is.
    previousComment: comment.editing.previousComment || comment.body,
    isEditing: false,
    error: false,
    submitting: true
  }
});

const resetParentEditCommentError = (comment) => ({
  ...comment,
  editing: {
    ...comment.editing,
    error: false
  }
});

const resetChildEditCommentError = (comment) => ({
  ...comment,
  editing: {
    ...comment.editing,
    error: false
  }
});

/**
 * This will amend an edited comment in the store
 * when the API returns a successful response.
 * It will replace the edited comment with
 * the comment from the API
 * and will clear the previous comment data
 * so that there won't be any discrepancies.
 * @param {Object} comment Comment object
 * @param {String} commentText Comment text to replace the comment, though it should normally be the same.
 */
const updateSuccessfullyEditedComment = (apiComment) => (
  comment,
  commentText
) => ({
  ...comment,
  ...apiComment,
  // We dont get all the children comments from the api after editing a comment
  // so instead of overwriting them we keep the old children comments
  // from the comment object.
  children: comment.children,
  body: commentText,
  editing: {
    previousComment: null,
    isEditing: false,
    error: false,
    submitting: false
  },
  translation: {
    ...comment.translation,
    isTranslated: false,
    previousComment: null
  }
});

/**
 * This will amend an edited comment
 * to revert the text back to its pre-edited form
 * and flag it with an error state
 * in case the API request fails.
 * @param {Object} comment Comment object
 */
const updateCommentAfterEditingFailure = (comment) => ({
  ...comment,
  editing: {
    ...comment.editing,
    isEditing: true,
    error: true,
    submitting: false
  }
});

const updateReportedStatus = (comment) => ({
  ...comment,
  reported_by_me: !comment.reported_by_me
});

export const deleteCommentRequest = (state, action) => {
  const {
    payload: { commentId }
  } = action;
  return {
    ...state,
    comments: updateCommentById(
      state.comments,
      commentId,
      updateDeleteRequestComment
    )
  };
};

export const deleteCommentFailure = (state, action) => {
  const {
    payload: { commentId }
  } = action;
  return {
    ...state,
    comments: updateCommentById(
      state.comments,
      commentId,
      updateDeleteFailureComment
    )
  };
};

export const deleteCommentSuccess = (state, action) => {
  const {
    payload: { comment }
  } = action;
  return {
    ...state,
    comments: updateCommentById(
      state.comments,
      comment.id,
      updateDeleteSuccessComment
    )
  };
};

export const deleteReplyRequest = (state, action) => {
  const {
    payload: { commentId, rootId }
  } = action;
  return {
    ...state,
    comments: updateChildCommentById(
      state.comments,
      rootId,
      commentId,
      updateDeleteRequestComment
    )
  };
};

export const deleteReplyFailure = (state, action) => {
  const {
    payload: { commentId, rootId }
  } = action;
  return {
    ...state,
    comments: updateChildCommentById(
      state.comments,
      rootId,
      commentId,
      updateDeleteFailureComment
    )
  };
};

export const deleteReplySuccess = (state, action) => {
  const {
    payload: { reply, rootId }
  } = action;
  return {
    ...state,
    comments: updateChildCommentById(
      state.comments,
      rootId,
      reply.id,
      updateDeleteSuccessComment
    )
  };
};

export const toggleCommentEditing = (state, action) => {
  const {
    payload: { commentId, rootId }
  } = action;

  return {
    ...state,
    comments: rootId
      ? updateChildCommentById(
          state.comments,
          rootId,
          commentId,
          toggleEditingState
        )
      : updateCommentById(state.comments, commentId, toggleEditingState)
  };
};

export const editParentRequest = (state, action) => {
  const {
    payload: { commentId, commentText }
  } = action;

  return {
    ...state,
    comments: updateCommentById(
      state.comments,
      commentId,
      updateEditedComment,
      commentText
    )
  };
};

export const editChildRequest = (state, action) => {
  const {
    payload: { commentId, rootId, commentText }
  } = action;

  return {
    ...state,
    comments: updateChildCommentById(
      state.comments,
      rootId,
      commentId,
      updateEditedComment,
      commentText
    )
  };
};

export const editParentSuccess = (state, action) => {
  const {
    payload: { comment }
  } = action;
  const { id: commentId, body: commentText } = comment;

  return {
    ...state,
    comments: updateCommentById(
      state.comments,
      commentId,
      updateSuccessfullyEditedComment(comment),
      commentText
    )
  };
};

export const editChildSuccess = (state, action) => {
  const {
    payload: { comment }
  } = action;
  const {
    id: commentId,
    body: commentText,
    ancestry: { root_comment_id: rootId } = {}
  } = comment;
  return {
    ...state,
    comments: updateChildCommentById(
      state.comments,
      rootId,
      commentId,
      updateSuccessfullyEditedComment(comment),
      commentText
    )
  };
};

export const editParentFailure = (state, action) => {
  const {
    payload: { commentId }
  } = action;

  return {
    ...state,
    comments: updateCommentById(
      state.comments,
      commentId,
      updateCommentAfterEditingFailure
    )
  };
};

export const editChildFailure = (state, action) => {
  const {
    payload: { commentId, rootId }
  } = action;

  return {
    ...state,
    comments: updateChildCommentById(
      state.comments,
      rootId,
      commentId,
      updateCommentAfterEditingFailure
    )
  };
};

/**
 * Resets the error state for an edited coment.
 * This can either be a parent comment or a child comment
 * @param {Object} state
 * @param {Object{ parentId: Number, commentId: Number }} action
 */
export const clearEditCommentError = (state, action) => {
  const { parentId, commentId } = action.payload;
  const updatedcomments = parentId
    ? updateChildCommentById(
        state.comments,
        parentId,
        commentId,
        resetChildEditCommentError
      )
    : updateCommentById(state.comments, commentId, resetParentEditCommentError);
  return {
    ...state,
    comments: updatedcomments
  };
};

export const clearPostCommentError = (state) => {
  return {
    ...state,
    error: false,
    errorType: null
  };
};

export const clearPostReplyError = (state, action) => {
  const {
    payload: { rootId }
  } = action;
  const { comments } = state;
  return {
    ...state,
    comments: clearReplyErrors({ comments, rootId })
  };
};

export const reportCommentReducer = (state, action) => {
  const {
    payload: { commentId }
  } = action;
  const { comments } = state;
  return {
    ...state,
    comments: updateCommentById(comments, commentId, updateReportedStatus),
    error: false
  };
};

export const reportReplyReducer = (state, action) => {
  const {
    payload: { rootId, commentId }
  } = action;
  const { comments } = state;
  return {
    ...state,
    comments: updateChildCommentById(
      comments,
      rootId,
      commentId,
      updateReportedStatus
    ),
    error: false
  };
};

export const loadMoreParentCommentsRequest = (state) => {
  return {
    ...state,
    isLoadingMore: true,
    loadMoreError: false
  };
};

export const loadMoreParentCommentsSuccess = (state, action) => {
  const {
    payload: { comments, isLastPage }
  } = action;
  const mergedComments = state.comments.concat(
    addStatusProps(comments, INITIAL_STATUS_PROPS)
  );

  return {
    ...state,
    comments: mergedComments,
    isLoadingMore: false,
    loadMoreError: false,
    isLastPage
  };
};

export const loadMoreParentCommentsFailure = (state) => {
  return {
    ...state,
    isLoadingMore: false,
    loadMoreError: true
  };
};

export const loadMoreChildrenCommentsRequest = (state, action) => {
  const { parentId } = action.payload;
  return {
    ...state,
    comments: updateCommentById(
      state.comments,
      parentId,
      updateLoadMoreChildrenCommentsRequest
    )
  };
};

export const loadMoreChildrenCommentsSuccess = (state, action) => {
  const { comments: newChildComments, parentId, isLastPage } = action.payload;
  const updatedComments = state.comments.map((comment) => {
    if (comment.id === parentId) {
      // Comments are not normalised so we need to ensure we remove any duplicates.
      const dedupedComments = comment.children
        .concat(newChildComments)
        .filter((outerComment, index, self) => {
          return (
            index ===
            self.findIndex(
              (innerComment) => innerComment.id === outerComment.id
            )
          );
        });
      return {
        ...comment,
        children: dedupedComments,
        childLoading: {
          ...comment.childLoading,
          isLoadingMore: false,
          loadMoreError: false,
          isLastPage
        }
      };
    }

    return comment;
  });

  return {
    ...state,
    comments: updatedComments
  };
};

export const loadMoreChildrenCommentsFailure = (state, action) => {
  const { parentId, totalCount } = action.payload;
  const updatedComments = state.comments.map((comment) => {
    if (comment.id === parentId) {
      return {
        ...comment,
        totalCount,
        childLoading: {
          ...comment.childLoading,
          isLoadingMore: false,
          loadMoreError: true
        }
      };
    }

    return comment;
  });

  return {
    ...state,
    comments: updatedComments
  };
};

export default createReducer(INITIAL_STATE, {
  [FETCH_COMMENTS_REQUEST]: fetchCommentsRequest,
  [FETCH_COMMENTS_SUCCESS]: fetchCommentsSuccess,
  [FETCH_COMMENTS_FAILURE]: fetchCommentsFailure,
  [FETCH_PARENT_COMMENTS_REQUEST]: fetchParentCommentsRequest,
  [FETCH_PARENT_COMMENTS_SUCCESS]: fetchParentCommentsSuccess,
  [FETCH_PARENT_COMMENTS_FAILURE]: fetchParentCommentsFailure,
  [FETCH_CHILD_COMMENTS_REQUEST]: fetchChildCommentsRequest,
  [CANCEL_CHILD_COMMENTS_REQUEST]: cancelChildCommentsRequest,
  [FETCH_CHILD_COMMENTS_SUCCESS]: fetchChildCommentsSuccess,
  [FETCH_CHILD_COMMENTS_FAILURE]: fetchChildCommentsFailure,
  [LIKE_COMMENT_REQUEST]: likeCommentRequest,
  [LIKE_COMMENT_SUCCESS]: likeCommentSuccess,
  [LIKE_COMMENT_FAILURE]: likeCommentFailure,
  [REPORT_COMMENT_REQUEST]: reportCommentReducer,
  [REPORT_COMMENT_FAILURE]: reportCommentReducer,
  [REPORT_REPLY_REQUEST]: reportReplyReducer,
  [REPORT_REPLY_FAILURE]: reportReplyReducer,
  [UNLIKE_COMMENT_REQUEST]: unlikeCommentRequest,
  [UNLIKE_COMMENT_SUCCESS]: unlikeCommentSuccess,
  [UNLIKE_COMMENT_FAILURE]: unlikeCommentFailure,
  [LIKE_CHILD_COMMENT_REQUEST]: likeChildCommentRequest,
  [LIKE_CHILD_COMMENT_SUCCESS]: likeChildCommentSuccess,
  [LIKE_CHILD_COMMENT_FAILURE]: likeChildCommentFailure,
  [UNLIKE_CHILD_COMMENT_REQUEST]: unlikeChildCommentRequest,
  [UNLIKE_CHILD_COMMENT_SUCCESS]: unlikeChildCommentSuccess,
  [UNLIKE_CHILD_COMMENT_FAILURE]: unlikeChildCommentFailure,
  [POST_COMMENT_REQUEST]: postCommentRequest,
  [POST_COMMENT_SUCCESS]: postCommentSuccess,
  [POST_COMMENT_FAILURE]: postCommentFailure,
  [POST_REPLY_REQUEST]: postReplyRequest,
  [POST_REPLY_SUCCESS]: postReplySuccess,
  [POST_REPLY_FAILURE]: postReplyFailure,
  [DELETE_COMMENT_REQUEST]: deleteCommentRequest,
  [DELETE_COMMENT_FAILURE]: deleteCommentFailure,
  [DELETE_COMMENT_SUCCESS]: deleteCommentSuccess,
  [DELETE_REPLY_REQUEST]: deleteReplyRequest,
  [DELETE_REPLY_FAILURE]: deleteReplyFailure,
  [DELETE_REPLY_SUCCESS]: deleteReplySuccess,
  [TOGGLE_COMMENT_EDITING]: toggleCommentEditing,
  [EDIT_PARENT_REQUEST]: editParentRequest,
  [EDIT_PARENT_SUCCESS]: editParentSuccess,
  [EDIT_PARENT_FAILURE]: editParentFailure,
  [EDIT_CHILD_REQUEST]: editChildRequest,
  [EDIT_CHILD_SUCCESS]: editChildSuccess,
  [EDIT_CHILD_FAILURE]: editChildFailure,
  [CLEAR_POST_COMMENT_ERROR]: clearPostCommentError,
  [CLEAR_POST_REPLY_ERROR]: clearPostReplyError,
  [CLEAR_EDIT_COMMENT_ERROR]: clearEditCommentError,
  [TRANSLATE_PARENT_REQUEST]: translateParentRequest,
  [TRANSLATE_PARENT_SUCCESS]: translateParentSuccess,
  [TRANSLATE_PARENT_FAILURE]: translateParentFailure,
  [TRANSLATE_CHILD_REQUEST]: translateChildRequest,
  [TRANSLATE_CHILD_SUCCESS]: translateChildSuccess,
  [TRANSLATE_CHILD_FAILURE]: translateChildFailure,
  [TOGGLE_PARENT_TRANSLATION]: toggleParentTranslation,
  [TOGGLE_CHILD_TRANSLATION]: toggleChildTranslation,
  [LOAD_MORE_PARENT_COMMENTS_REQUEST]: loadMoreParentCommentsRequest,
  [LOAD_MORE_CHILDREN_COMMENTS_REQUEST]: loadMoreChildrenCommentsRequest,
  [LOAD_MORE_PARENT_COMMENTS_SUCCESS]: loadMoreParentCommentsSuccess,
  [LOAD_MORE_PARENT_COMMENTS_FAILURE]: loadMoreParentCommentsFailure,
  [LOAD_MORE_CHILDREN_COMMENTS_SUCCESS]: loadMoreChildrenCommentsSuccess,
  [LOAD_MORE_CHILDREN_COMMENTS_FAILURE]: loadMoreChildrenCommentsFailure
});
